import {
  authTokenStorage,
  signOut,
  useIsLoggedIn,
  userStorage,
} from "features/auth/AuthUtils";
import { usePing } from "gen/endpoints/frontend/frontend";
import { TokenObtainPairResponse } from "gen/model/tokenObtainPairResponse";
import loglevel from "loglevel";
import { useCallback, useEffect, useState } from "react";
import { ALLOWED_TLDS } from "shared/config/AppConfigUtils";
import { isSafari } from "shared/utils/BrowserUtils";
import { tryParseUrl } from "shared/utils/UrlUtils";

const log = loglevel.getLogger("AuthIFramePage");

/**
 * This is the "server" for exchanging auth tokens with a customer subdomain.
 * @see {@link https://www.notion.so/maybern/Customer-Subdomains-22e3767e933f4f68bf6d9405cbb20c01} for more information.
 * @see {@link AuthIFrameClientPage} for the client.
 */
export const AuthIFramePage: React.FC = () => {
  const isLoggedIn = useIsLoggedIn();

  const [authTokens] = authTokenStorage.use();
  const [userData] = userStorage.use();

  // validates the token is still valid
  const { isLoading, error } = usePing({
    query: {
      enabled: isLoggedIn,
      throwOnError: false,
    },
  });

  const [didSendMessage, setDidSendMessage] = useState(false);

  const processSendMessage = useCallback(async () => {
    const referrerUrl = tryParseUrl(document.referrer);
    if (!referrerUrl) {
      log.error("invalid referrer url", referrerUrl);
      return;
    }

    const isValidReferrer = ALLOWED_TLDS.find((tld) =>
      referrerUrl.hostname.endsWith(`.${tld}`),
    );
    if (!isValidReferrer) {
      log.error("invalid referrer hostname", referrerUrl.hostname);
      return;
    }

    if (isLoading || didSendMessage) {
      return;
    }

    setDidSendMessage(true);

    const messageOptions: WindowPostMessageOptions = {
      targetOrigin: document.referrer,
    };

    // see note in AuthIFrameClientPage about why safari is treated differently
    const receiverWindow: Window = isSafari ? window.opener : window.parent;

    if (!isLoggedIn || error) {
      receiverWindow.postMessage(
        { type: "mb-auth-logged-out" },
        messageOptions,
      );
      return;
    }

    if (!authTokens || !userData) {
      log.error(
        `bad local storage state: (authTokens: ${!!authTokens}, userData: ${!!userData})`,
      );
      await signOut(true);
      receiverWindow.postMessage(
        { type: "mb-auth-logged-out" },
        messageOptions,
      );
      return;
    }

    const tokenData: TokenObtainPairResponse = {
      access: authTokens.accessToken,
      refresh: authTokens.refreshToken,
      user_id: userData.userId,
      email: userData.email,
      first_name: userData.firstName,
      last_name: userData.lastName,
      intercom_user_hash: userData.intercomUserHash,
      clients: [],
    };
    receiverWindow.postMessage(
      { type: "mb-auth-logged-in", tokenData },
      messageOptions,
    );
  }, [authTokens, didSendMessage, error, isLoading, isLoggedIn, userData]);

  useEffect(() => {
    void processSendMessage();
  }, [processSendMessage]);

  return null;
};
